import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CEFR_LEVELS, type CEFRLevel } from '../../../types';

interface CEFRLevelStepProps {
  onSelect: (level: CEFRLevel) => void;
  onNext: () => void;
  onBack: () => void;
  selectedLevel: CEFRLevel | null;
}

export function CEFRLevelStep({
  onSelect,
  onNext,
  onBack,
  selectedLevel,
}: CEFRLevelStepProps) {
  const { t } = useTranslation('common');
  
  const getLevelDescription = (level: CEFRLevel) => ({
    title: t(`onboarding.cefrLevel.levels.${level}.title`),
    description: t(`onboarding.cefrLevel.levels.${level}.description`),
  });

  return (
    <div className="cefr-level-step">
      <div className="step-header mb-8 text-center">
        <h3 className="text-2xl font-semibold text-gray-900 mb-2">
          {t('onboarding.cefrLevel.title')}
        </h3>
        <p className="text-gray-600">
          {t('onboarding.cefrLevel.description')}
        </p>
      </div>

      <div className="levels-grid grid gap-3 max-w-lg mx-auto">
        {CEFR_LEVELS.map(level => {
          const info = getLevelDescription(level);
          const isSelected = selectedLevel === level;

          return (
            <button
              key={level}
              onClick={() => onSelect(level)}
              className={`level-card flex items-center justify-between rounded-lg border-2 p-4 text-left transition-all ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              <div className="level-info flex-1">
                <div className="level-code-title flex items-center gap-3">
                  <span
                    className={`level-badge flex h-10 w-10 items-center justify-center rounded-full font-bold ${
                      isSelected
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {level}
                  </span>
                  <div>
                    <div className="text-base font-semibold text-gray-900">
                      {info.title}
                    </div>
                    <div className="text-sm text-gray-600">
                      {info.description}
                    </div>
                  </div>
                </div>
              </div>
              {isSelected && (
                <div className="check-mark ml-4">
                  <svg
                    className="h-6 w-6 text-primary"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="navigation-buttons mt-8 flex justify-between">
        <button
          onClick={onBack}
          className="back-btn flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
        <button
          onClick={onNext}
          className="next-btn rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 transition-colors"
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  );
}

